import {DataLoader} from "@/components/general/ui/DataLoader"
import {ThreadWithProfiles} from "@/utils/graphQL/queries/threads/shared"
import {NavigatorTableView} from "@/components/archive/NavigatorTableView"
import {VIEW_MODES, ViewMode} from "../archiveTypes"

interface ThreadListProps {
  monthThreads?: Map<string, ThreadWithProfiles[]>
  searchThreads?: ThreadWithProfiles[]

  // Info about current state
  mode: ViewMode

  // Loading states
  isLoading: boolean
  hasNextPage: boolean

  // Ref for infinite scrolling
  loadMoreRef: (node?: Element | null) => void
}

const ThreadList = ({
  monthThreads,
  searchThreads,
  mode,
  isLoading,
  hasNextPage,
  loadMoreRef,
}: ThreadListProps) => {
  if (mode === VIEW_MODES.MONTH_VIEW && monthThreads && monthThreads.size > 0) {
    // Month view
    return (
      <>
        {Array.from(monthThreads.entries())
          .sort(([dateA], [dateB]) => {
            // Parse month names to get proper chronological order
            const monthNames = [
              "January",
              "February",
              "March",
              "April",
              "May",
              "June",
              "July",
              "August",
              "September",
              "October",
              "November",
              "December",
            ]

            const [monthA, yearA] = dateA.split(" ")
            const [monthB, yearB] = dateB.split(" ")

            // Compare years first
            if (yearA !== yearB) {
              return Number(yearB) - Number(yearA) // Descending by year
            }

            // If years are the same, compare months
            return monthNames.indexOf(monthB) - monthNames.indexOf(monthA) // Descending by month
          })
          .map(([date, thread], idx) => {
            return (
              <NavigatorTableView
                key={idx}
                threads={thread}
                date={date}
                mode={mode}
              />
            )
          })}
        {/* Infinite scroll trigger */}
        <div ref={loadMoreRef} className="h-10 w-full">
          {isLoading && monthThreads.size > 0 && <DataLoader />}
          {/* Loader during fetchMore, check monthThreads.size to avoid showing on initial load flicker */}
          {!isLoading && !hasNextPage && monthThreads.size > 0 && (
            <p className="text-center text-gray-500">No more threads</p>
          )}
        </div>
      </>
    )
  } else if (
    // Search results view
    mode === VIEW_MODES.SEARCH_RESULTS &&
    searchThreads &&
    searchThreads.length > 0
  ) {
    return (
      <>
        <NavigatorTableView
          key={VIEW_MODES.SEARCH_RESULTS}
          threads={searchThreads}
          date={"Search Results"}
          mode={mode}
        />

        {/* Infinite scroll trigger */}
        <div ref={loadMoreRef} className="h-10 w-full">
          {isLoading && searchThreads.length > 0 && <DataLoader />}
          {!isLoading && !hasNextPage && searchThreads.length > 0 && (
            <p className="text-center text-gray-500">No more results</p>
          )}
        </div>
      </>
    )
  } else if (
    // Loading state
    isLoading &&
    ((mode === VIEW_MODES.MONTH_VIEW && !monthThreads?.size) ||
      (mode === VIEW_MODES.SEARCH_RESULTS && !searchThreads?.length))
  ) {
    return <DataLoader />
  } else {
    // Empty state
    return (
      <div className="p-4">
        <p className="text-default-500">No threads found</p>
      </div>
    )
  }
}

export default ThreadList
